import { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import Input from 'input-format/react';
import { TextInput } from '../../v2/forms/TextInput/index.js';
import { Select } from '../../v2/forms/Select/index.js';
import { AsYouType, parsePhoneNumberCharacter } from 'libphonenumber-js/min';
import { useControlledWarning, useCountries, transformValue, getCountryFromPartialInputValue, migrateNationalNumber, toE164, getCountryFromPartialE164 } from '../utils.js';
import { Container, SelectContainer, InputContainer } from '../styled.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var CountryAndInput = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var id = props.id,
    controlledValue = props.value,
    defaultValue = props.defaultValue,
    _props$defaultCountry = props.defaultCountry,
    defaultCountry = _props$defaultCountry === void 0 ? 'SE' : _props$defaultCountry,
    translations = props.countries,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$invalid = props.invalid,
    invalid = _props$invalid === void 0 ? false : _props$invalid,
    onChange = props.onChange,
    describedBy = props.describedBy,
    onBlur = props.onBlur,
    required = props.required,
    name = props.name;
  var _useRef = useRef(Boolean(controlledValue)),
    controlled = _useRef.current;
  useControlledWarning(controlled, controlledValue);
  var countries = useCountries(translations);
  var _useState = useState(defaultCountry),
    country = _useState[0],
    setCountry = _useState[1];
  var _useState2 = useState(controlledValue || defaultValue),
    outputValue = _useState2[0],
    setOutputValue = _useState2[1];
  var _useState3 = useState(function () {
      return transformValue(outputValue);
    }),
    inputValue = _useState3[0],
    setInputValue = _useState3[1];
  var format = useCallback(function (value) {
    var asYouType = new AsYouType(country);
    var text = asYouType.input(value !== null && value !== void 0 ? value : '');
    var template = asYouType.getTemplate();
    return {
      text: text,
      template: template
    };
  }, [country]);
  var handleChange = useCallback(function (parsedValue) {
    if (typeof parsedValue !== 'string') {
      return;
    }
    var nextCountry = getCountryFromPartialInputValue(country, parsedValue);
    var asYouType = new AsYouType(nextCountry);
    asYouType.input(parsedValue);
    var phoneNumber = asYouType.getNumber();
    setOutputValue(phoneNumber && phoneNumber.number || '');
    setInputValue(parsedValue);
    setCountry(nextCountry);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onChange, country]);
  var handleSelect = useCallback(function (event) {
    var nextCountry = event.target.value;
    var nextInputValue = migrateNationalNumber(nextCountry, inputValue);
    var nextOutputValue = toE164(nextCountry, nextInputValue);
    setCountry(nextCountry);
    setInputValue(nextInputValue);
    setOutputValue(nextOutputValue);
  }, [inputValue]);
  useEffect(function () {
    var updatedFromControlledValue = controlled && controlledValue !== outputValue;
    if (updatedFromControlledValue) {
      var nextCountry = getCountryFromPartialE164(country, controlledValue);
      var nextInputValue = transformValue(controlledValue);
      setInputValue(nextInputValue);
      setOutputValue(controlledValue);
      setCountry(nextCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue]);
  useEffect(function () {
    if (onChange) {
      onChange(outputValue || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputValue]);
  return /*#__PURE__*/jsxs(Container, {
    children: [/*#__PURE__*/jsx(SelectContainer, {
      children: /*#__PURE__*/jsx(Select, {
        value: country,
        onChange: handleSelect,
        disabled: disabled,
        invalid: invalid,
        stretch: true,
        children: countries.map(function (_ref) {
          var code = _ref.code,
            label = _ref.label;
          return /*#__PURE__*/jsx("option", {
            value: code,
            children: label
          }, code);
        })
      })
    }), /*#__PURE__*/jsx(InputContainer, {
      children: /*#__PURE__*/jsx(Input
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error types are incorrect in "input-format/react", maybe consider a different library/solution?
      , {
        inputComponent: TextInput,
        format: format,
        parse: parsePhoneNumberCharacter,
        ref: forwardedRef,
        value: inputValue,
        onChange: handleChange,
        disabled: disabled,
        invalid: invalid,
        stretch: true,
        "aria-describedby": describedBy,
        onBlur: onBlur,
        required: required,
        name: name,
        id: id
      })
    })]
  });
});

export { CountryAndInput };
